import { ChangeEvent, FC } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Moment } from "moment";
import { MiDetailFields } from "@miplatform/components";
import { DATE_TIME_FORMATS, INPUT_TYPES } from "@miplatform/constants";
import { CalendarType } from "@miplatform/types";
import { getDate } from "@miplatform/utils";
import { Divider, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  ICalendarSetData,
  ICalendarWorkCapacityConfig,
} from "../../ConfigCalendarSetList.types";

export const CalendarDetailsTabView: FC<CalendarDetailsTabViewProps> = ({
  form,
  extraTabProps: { isReadOnly } = {},
}: CalendarDetailsTabViewProps) => {
  const { control, setError, setValue, getValues, clearErrors, watch } = form;

  const {
    canScheduleOutsideDayTime,
    workCapacities,
    capacityWarningThreshold,
    capacityCriticalThreshold,
  } = watch();

  const calendarType = getValues("calendarConfigs.0.calendarType");
  const requireManagerOverrideForOutsideDayTime = useWatch({
    control,
    name: "requireManagerOverrideForOutsideDayTime",
  });

  const handleWorkCapacityChange = (
    newStartTime: Moment,
    newEndTime: Moment
  ) => {
    const start = getDate(newStartTime);
    const end = getDate(newEndTime);
    const hoursDifference = end.diff(start, "hours", true);

    if (hoursDifference <= 0) {
      setValue("workCapacities", {});
      return;
    }

    const availableHours = Math.round(Math.abs(hoursDifference));

    setValue(
      "workCapacities",
      Object.keys(workCapacities).reduce((acc, userId) => {
        const userCapacities = workCapacities[userId];

        const currentTotal = Object.values(userCapacities).reduce(
          (s, c) => s + c.amount,
          0
        );

        if (currentTotal <= availableHours) {
          acc[userId] = userCapacities;
          return acc;
        }
        const sortedTemplates = Object.entries(userCapacities).sort(
          ([, a], [, b]) => a.displayOrder - b.displayOrder
        );

        let remainingAvailable = availableHours;

        const trimmedUserMap = sortedTemplates.reduce(
          (userAcc, [templateId, config]) => {
            if (remainingAvailable <= 0) return userAcc;

            const takeAmount = Math.min(config.amount, remainingAvailable);

            userAcc[templateId] = {
              ...config,
              amount: takeAmount,
            };

            remainingAvailable -= takeAmount;
            return userAcc;
          },
          {} as Record<string, ICalendarWorkCapacityConfig>
        );

        acc[userId] = trimmedUserMap;
        return acc;
      }, {} as Record<string, Record<string, ICalendarWorkCapacityConfig>>)
    );
  };

  const setTime = (
    time: Moment,
    label: string,
    key: keyof ICalendarSetData
  ) => {
    if (!time?.isValid()) {
      setError(key, { type: "manual", message: `${label} is required.` });
      return;
    }

    time.seconds(0);
    const otherTimeKey = key === "dayStartTime" ? "dayEndTime" : "dayStartTime";
    const otherTime = getDate(getValues(otherTimeKey));
    otherTime.seconds(0);

    const timeDiff =
      key === "dayStartTime"
        ? otherTime.diff(time, "hours")
        : time.diff(otherTime, "hours");

    if (timeDiff <= 0) {
      setError(key, {
        message: `${label} must be ${
          key === "dayStartTime" ? "earlier" : "later"
        } than the ${key === "dayStartTime" ? "End Time" : "Start Time"}.`,
      });
      setValue(key, time, { shouldDirty: true });
      return;
    }

    setValue(key, time, { shouldDirty: true, shouldValidate: true });
    clearErrors(key);
    clearErrors(otherTimeKey);
  };

  const calendarDetailFields = [
    {
      label: "Calendar Name",
      name: "name",
      formControl: control,
      required: true,
      inputProps: { maxLength: 255 },
      handleChange: ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setValue("name", value);
        setValue("calendarConfigs.0.name", value, {
          shouldDirty: true,
          shouldValidate: true,
        });
        if (value.trim() === "") {
          setError("name", {
            message: "Calendar Name is required.",
          });
        } else {
          clearErrors("name");
        }
      },
      disabled: isReadOnly,
    },
  ];

  const calendarDayTimeFields = [
    {
      label: "Day Start Time",
      name: "dayStartTime",
      formControl: control,
      fieldType: INPUT_TYPES.TIME_PICKER,
      format: DATE_TIME_FORMATS.FORMAT_12_HOUR,
      required: true,
      strictInterval: true,
      timeSteps: { minutes: 60 },
      disabled: isReadOnly,
      onChange: (time: Moment) => {
        setTime(time, "Start Time", "dayStartTime");
        handleWorkCapacityChange(time, getValues("dayEndTime") as Moment);
      },
    },
    {
      label: "Day End Time",
      name: "dayEndTime",
      formControl: control,
      fieldType: INPUT_TYPES.TIME_PICKER,
      format: DATE_TIME_FORMATS.FORMAT_12_HOUR,
      required: true,
      strictInterval: true,
      timeSteps: { minutes: 60 },
      disabled: isReadOnly,
      onChange: (time: Moment) => {
        setTime(time, "End Time", "dayEndTime");
        handleWorkCapacityChange(getValues("dayStartTime") as Moment, time);
      },
    },
  ];

  const schedulingRulesFields = [
    {
      label: "Allow for scheduling orders outside of day start to day end",
      name: "canScheduleOutsideDayTime",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      required: true,
      disabled: isReadOnly,
      handleChange: ({
        target: { checked },
      }: ChangeEvent<HTMLInputElement>) => {
        setValue("canScheduleOutsideDayTime", checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (!checked) {
          setValue("requireManagerOverrideForOutsideDayTime", false, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      },
    },
    {
      label: "Require manager override",
      name: "requireManagerOverrideForOutsideDayTime",
      formControl: control,
      value: requireManagerOverrideForOutsideDayTime,
      fieldType: INPUT_TYPES.SWITCH,
      required: true,
      disabled: isReadOnly || !canScheduleOutsideDayTime,
    },
    {
      label: "Capacity Warning Threshold",
      name: "capacityWarningThreshold",
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      width: "50%",
      required: true,
      disabled: isReadOnly,
      value: capacityWarningThreshold,
      setValue: (value: string) => {
        setValue("capacityWarningThreshold", Number(value), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      label: "Capacity Critical Threshold",
      name: "capacityCriticalThreshold",
      width: "50%",
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      required: true,
      disabled: isReadOnly,
      value: capacityCriticalThreshold,
      setValue: (value: string) => {
        if (Number(value) >= capacityWarningThreshold) {
          setError("capacityCriticalThreshold", {
            message:
              "Capacity Critical Threshold cannot come before Capacity Warning Threshold.",
          });
          return;
        }
        clearErrors("capacityCriticalThreshold");
        setValue("capacityCriticalThreshold", Number(value), {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
  ];

  return (
    <>
      <ConfigAccordionHeader title="Calendar Details">
        <MiDetailFields detailFields={calendarDetailFields} />
      </ConfigAccordionHeader>
      {calendarType !== CalendarType.Kanban && (
        <ConfigAccordionHeader title="Time Settings">
          <MiDetailFields detailFields={calendarDayTimeFields} />
          <Divider sx={{ my: 3 }} />
          <Typography fontWeight={600} color="primary.main">
            Scheduling Rules
          </Typography>
          <MiDetailFields detailFields={schedulingRulesFields} />
        </ConfigAccordionHeader>
      )}
    </>
  );
};

interface CalendarDetailsTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
